// store/modules/requests.js
import api from '@/api/api'

export default {
    namespaced: true,
    state: {
        requests: [],
        currentRequest: null,
        loading: false,
        error: null,
        pagination: {
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: 50
        },
        search: '',
        statusFilter: ''
    },
    getters: {
        allRequests: (state) => state.requests,
        currentRequest: (state) => state.currentRequest,
        loading: (state) => state.loading,
        error: (state) => state.error,
        pagination: (state) => state.pagination,
        search: (state) => state.search,
        statusFilter: (state) => state.statusFilter
    },
    mutations: {
        SET_REQUESTS(state, requests) {
            state.requests = requests
        },
        SET_CURRENT_REQUEST(state, request) {
            state.currentRequest = request
        },
        ADD_REQUEST(state, request) {
            state.requests.push(request)
        },
        SET_LOADING(state, status) {
            state.loading = status
        },
        SET_ERROR(state, error) {
            state.error = error
        },
        CLEAR_ERROR(state) {
            state.error = null
        },
        SET_PAGINATION(state, pagination) {
            state.pagination = pagination;
        },
        SET_SEARCH(state, search) {
            state.search = search;
        },
        SET_STATUS_FILTER(state, status) {
            state.statusFilter = status;
        }
    },
    actions: {
        // Submit a new request
        async submitRequest({ commit }, formData) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                // Using axios through our api instance
                const response = await api.post('/requests', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })

                commit('ADD_REQUEST', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to submit request'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch all requests (admin only)
        async fetchRequests({ commit, state }, { page, search, status } = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                // Set search and status if provided
                if (search !== undefined) commit('SET_SEARCH', search);
                if (status !== undefined) commit('SET_STATUS_FILTER', status);

                // Use provided page or current page
                const currentPage = page || state.pagination.currentPage;

                // Build query params
                const params = {
                    page: currentPage,
                    limit: state.pagination.limit,
                    search: state.search,
                    status: state.statusFilter
                };

                const response = await api.get('/requests', { params });

                commit('SET_REQUESTS', response.data.data);

                // Update pagination state
                commit('SET_PAGINATION', {
                    currentPage: response.data.currentPage,
                    totalPages: response.data.totalPages,
                    total: response.data.total,
                    limit: state.pagination.limit
                });

                return response.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch requests';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Add action to change page
        async changePage({ dispatch }, page) {
            return dispatch('fetchRequests', { page });
        },

        // Add action for search
        async searchRequests({ dispatch }, search) {
            return dispatch('fetchRequests', { search, page: 1 }); // Reset to page 1 when searching
        },

        // Add action for filtering by status
        async filterByStatus({ dispatch }, status) {
            return dispatch('fetchRequests', { status, page: 1 }); // Reset to page 1 when filtering
        },

        // Fetch a single request by ID
        async fetchRequestById({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/requests/${id}`)

                commit('SET_CURRENT_REQUEST', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch request'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Update request status (admin only)
        async updateRequestStatus({ commit }, { id, status, statusNotes }) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.put(`/requests/${id}/status`, { status, statusNotes })

                commit('SET_CURRENT_REQUEST', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to update request status'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },
        // Get file download URL (admin only)
        async getFileDownloadUrl({ commit }, { id, filename }) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/requests/${id}/files/${filename}`)

                return response.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to get download URL'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },
        async fetchPublicRequestById({ commit }, id) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get(`/public/requests/${id}`);
                commit('SET_CURRENT_REQUEST', response.data.data);
                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch request';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        }
    }
}
